import { useState } from 'react';
import { MoreHorizontal, Plus, Users, X } from 'lucide-react';
import { uuidv7 } from '@vidmakr/shared';
import { Avatar, Button, Card, Menu, Select } from '@/components/ui';
import { NameDialog } from '@/components/Spaces';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { useSpaces } from '@/lib/spaces';

/** Groups of members, to give many people access to a space or folder at once. */
export function GroupsSection({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const { groups, groupMembers, members, grants, spaces, folders } = useSpaces(orgId);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; name: string; description: string } | null>(null);
  const nameOf = (userId: string) => members.find((m) => m.userId === userId)?.name || members.find((m) => m.userId === userId)?.email || 'Someone';
  const targetName = (id: string) => spaces.find((s) => s.id === id)?.name ?? folders.find((f) => f.id === id)?.name ?? 'a folder';
  return (
    <div className="mt-8" data-groups>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="flex items-center gap-2 font-semibold">
          <Users size={16} /> Groups
        </h2>
        <span className="text-sm text-muted">Give a whole team access to a space or folder at once.</span>
        <div className="flex-1" />
        {canManage ? (
          <Button size="sm" onClick={() => setCreating(true)} data-new-group>
            <Plus size={14} /> New group
          </Button>
        ) : null}
      </div>
      {groups.length === 0 ? (
        <Card className="p-5 text-sm text-muted">No groups yet.</Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {groups.map((g) => {
            const inGroup = groupMembers.filter((m) => m.groupId === g.id);
            const access = grants.filter((x) => x.principalKind === 'group' && x.principalId === g.id);
            const addable = members.filter((m) => !inGroup.some((x) => x.userId === m.userId));
            return (
              <Card key={g.id} className="p-4" data-group={g.id}>
                <div className="mb-2 flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{g.name}</div>
                    {g.description ? <div className="text-xs text-muted">{g.description}</div> : null}
                  </div>
                  {canManage ? (
                    <Menu
                      trigger={
                        <Button variant="ghost" size="icon-sm" aria-label="Group actions">
                          <MoreHorizontal size={14} />
                        </Button>
                      }
                      items={[
                        { label: 'Rename', onClick: () => setRenaming({ id: g.id, name: g.name, description: g.description }) },
                        'sep',
                        { label: 'Delete group', danger: true, onClick: () => void attempt(() => reducers().deleteGroup({ id: g.id }), 'Group deleted') },
                      ]}
                    />
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {inGroup.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1.5 rounded-full bg-panel-2 py-0.5 pl-0.5 pr-2 text-xs" data-group-member={m.userId}>
                      <Avatar name={nameOf(m.userId)} size={18} />
                      {nameOf(m.userId)}
                      {canManage ? (
                        <button aria-label="Remove from group" className="text-faint hover:text-fg" onClick={() => void attempt(() => reducers().setGroupMember({ groupId: g.id, userId: m.userId, present: false }))}>
                          <X size={11} />
                        </button>
                      ) : null}
                    </span>
                  ))}
                  {!inGroup.length ? <span className="text-xs text-faint">Nobody yet</span> : null}
                </div>
                {canManage && addable.length ? (
                  <Select
                    className="mt-3 h-8 w-full text-xs"
                    value=""
                    onChange={(e) => e.target.value && void attempt(() => reducers().setGroupMember({ groupId: g.id, userId: e.target.value, present: true }))}
                    data-group-add
                  >
                    <option value="">Add a member…</option>
                    {addable.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name || m.email}
                        {m.role === 'guest' ? ' (guest)' : ''}
                      </option>
                    ))}
                  </Select>
                ) : null}
                {access.length ? (
                  <div className="mt-3 text-[11px] text-faint">
                    Access to {access.map((a) => `${targetName(a.targetId)} (${a.role === 'editor' ? 'edit' : 'view'})`).join(', ')}
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}
      <NameDialog
        open={creating}
        title="New group"
        action="Create"
        onClose={() => setCreating(false)}
        onSave={(name) => attempt(() => reducers().createGroup({ id: uuidv7(), orgId, name, description: '' }), 'Group created')}
      />
      <NameDialog
        open={!!renaming}
        title="Rename group"
        action="Save"
        initial={renaming?.name ?? ''}
        onClose={() => setRenaming(null)}
        onSave={(name) => attempt(() => reducers().updateGroup({ id: renaming!.id, name, description: renaming!.description }))}
      />
    </div>
  );
}
